import { z } from "zod";

/**
 * Shared DTO cho Admin Referral & Cohort analytics (US-AE10.F4-0035 · @UC9093 referral-cohort-analytics).
 * MỘT NGUỒN SỰ THẬT cho api (S01) + admin web (S03). Projection CHỈ-ĐỌC từ nguồn có sẵn
 * (Referral status/referrer/referred + User.createdAt/AuthSession.createdAt) — DTO chỉ mô tả read-only aggregate;
 * KHÔNG field mutate. NO migration.
 *
 * BẤT BIẾN (invariants):
 * - C2/PII: top referrer danh tính (referrerRef) redact theo vai (SUPER_ADMIN/SUPPORT thấy; OPS/READ_ONLY
 *   → null → UI "—"). Counts (invited/converted) là aggregate → luôn hiển thị.
 * - E12-5 attribution: blocked self-referral = DERIVE referrerUserId==referredUserId (KHÔNG flag DB).
 *   reason = "self_referral" (hằng, không phải cột DB). App-Lane đã chặn ở đăng ký — portal chỉ LIỆT KÊ.
 * - `.strict()` mọi object đọc — chống field lạ trên wire.
 * - Read-only: KHÔNG DTO ghi/mutate. Cursor envelope `{ data, pageInfo }` cho blocked list.
 *
 * Công thức (Decision Record trong task PROGRESS.md §1-4):
 * - viralCoefficient K = converted / distinct-referrer (mẫu 0 ⇒ 0).
 * - retentionWN = |user cohort có AuthSession.createdAt trong cửa sổ tuần N| / cohortSize (0..1).
 */

// ============================================================
// @UC9093 — Referral stats (AC1)
// ============================================================

/**
 * KPI referral (@UC9093 AC1). pending/converted = count theo Referral.status. conversionRate + K =
 * number ratio [0..], KHÔNG phải tiền. viralCoefficient = converted / distinct-referrer (Decision §1).
 */
export const referralStatsSchema = z
  .object({
    /** Σ Referral.status = "pending". */
    pending: z.number().int().min(0),
    /** Σ Referral.status = "converted". */
    converted: z.number().int().min(0),
    /** Tổng số ca referral (mọi status) — mẫu số conversionRate. */
    total: z.number().int().min(0),
    /** converted / total (mẫu 0 ⇒ 0). Ratio [0..1]. */
    conversionRate: z.number().min(0),
    /** K = converted / distinct-referrer (Decision §1; mẫu 0 ⇒ 0). Ratio ≥ 0. */
    viralCoefficient: z.number().min(0),
  })
  .strict();

export type ReferralStats = z.infer<typeof referralStatsSchema>;

/**
 * Top referrer (@UC9093 AC1/AC6). referrerRef = danh tính redact theo vai (PII): {userId, name} khi
 * vai được xem, `null` khi redact (UI "—"). invited/converted = aggregate count LUÔN hiển thị.
 */
export const topReferrerRefSchema = z
  .object({
    userId: z.string(),
    /** fullName (có thể null nếu user chưa đặt tên). */
    name: z.string().nullable(),
  })
  .strict();

export type TopReferrerRef = z.infer<typeof topReferrerRefSchema>;

export const topReferrerSchema = z
  .object({
    /** Danh tính người mời — null khi redact theo vai (OPS/READ_ONLY). C2/PII. */
    referrerRef: topReferrerRefSchema.nullable(),
    /** Số ca đã mời (mọi status) của referrer này. */
    invited: z.number().int().min(0),
    /** Số ca converted của referrer này. */
    converted: z.number().int().min(0),
  })
  .strict();

export type TopReferrer = z.infer<typeof topReferrerSchema>;

// ============================================================
// @UC9093 — Blocked self-referral (AC2) — DERIVE referrer==referred
// ============================================================

/**
 * Ca self-referral bị chặn (@UC9093 AC2). DERIVE Referral.referrerUserId == referredUserId (KHÔNG có
 * flag/reason trong schema — Referral chỉ pending|converted). reason cố định "self_referral".
 * App-Lane E12-5 đã chặn lúc đăng ký; portal CHỈ LIỆT KÊ (read-only).
 */
export const blockedSelfReferralSchema = z
  .object({
    referralId: z.string(),
    /** userRef = danh tính user tự-giới-thiệu — null khi redact theo vai (PII). */
    userRef: topReferrerRefSchema.nullable(),
    /** Kênh (nếu có) — Referral.channel. */
    channel: z.string().nullable(),
    /** Lý do cố định (không phải cột DB) — E12-5 self-referral. */
    reason: z.literal("self_referral"),
    createdAt: z.string(),
  })
  .strict();

export type BlockedSelfReferral = z.infer<typeof blockedSelfReferralSchema>;

// ============================================================
// @UC9093 — Referral analytics response envelope
// ============================================================

/**
 * Response GET /admin/analytics/referrals: stats + topReferrers + blocked self-referral list (cursor).
 * Envelope blocked khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext).
 */
export const referralAnalyticsResponseSchema = z
  .object({
    stats: referralStatsSchema,
    /** Top N referrer theo converted (redacted ref). Aggregate — KHÔNG cursor (giới hạn cứng). */
    topReferrers: z.array(topReferrerSchema),
    /** Ca self-referral bị chặn — cursor pagination (list có thể dài). */
    blockedSelfReferrals: z
      .object({
        data: z.array(blockedSelfReferralSchema),
        pageInfo: z
          .object({
            hasNext: z.boolean(),
            nextCursor: z.string().nullable(),
          })
          .strict(),
      })
      .strict(),
  })
  .strict();

export type ReferralAnalyticsResponse = z.infer<typeof referralAnalyticsResponseSchema>;

/** Query GET /admin/analytics/referrals — cursor + limit cho blocked list.
 * `format=csv` ⇒ export CSV top referrers (US-AE-IMP.F5-0116 · @UC9146); vắng ⇒ JSON. */
export const referralAnalyticsQuerySchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    format: z.enum(["csv"]).optional(),
  })
  .strict();

export type ReferralAnalyticsQuery = z.infer<typeof referralAnalyticsQuerySchema>;

// ============================================================
// @UC9093 — Cohort retention (AC3)
// ============================================================

/**
 * Cohort retention (@UC9093 AC3). signupWeek = ISO tuần (thứ 2 đầu tuần UTC) của User.createdAt.
 * retentionWN [0..1] = |user cohort có AuthSession.createdAt trong cửa sổ tuần N| / size (Decision §2).
 */
export const cohortRetentionSchema = z
  .object({
    /** Tuần signup ISO (YYYY-Www hoặc ISO date đầu tuần — mốc thứ 2). */
    signupWeek: z.string(),
    /** Số user trong cohort (mẫu số retention). */
    size: z.number().int().min(0),
    /** Retention tuần 2 (7..14 ngày sau tuần signup) [0..1]. */
    retentionW2: z.number().min(0).max(1),
    /** Retention tuần 3 [0..1]. */
    retentionW3: z.number().min(0).max(1),
    /** Retention tuần 4 [0..1]. */
    retentionW4: z.number().min(0).max(1),
  })
  .strict();

export type CohortRetention = z.infer<typeof cohortRetentionSchema>;

/** Response GET /admin/analytics/cohorts: mảng cohort theo tuần signup (mới→cũ). */
export const cohortAnalyticsResponseSchema = z
  .object({
    cohorts: z.array(cohortRetentionSchema),
  })
  .strict();

export type CohortAnalyticsResponse = z.infer<typeof cohortAnalyticsResponseSchema>;
